import { MoreVert } from '@mui/icons-material';
import { Theme, useMediaQuery } from '@mui/material';
import React from 'react';
import { useMenuLogic } from '~/Hooks';
import { Button, IconButton, Menu, MenuItem } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';

export enum OverflowMenuItemType {
  PRIMARY,
  SECONDARY,
  ALWAYS_HIDDEN,
}

export interface OverflowingMenuItem {
  label: string;
  type: OverflowMenuItemType;
  onClick: () => void;
  hide?: boolean;
}

export enum OverflowingMenuSensitivity {
  LOW,
  MEDIUM,
  HIGH,
}

export interface IOverflowingMenuProps {
  menuItems: OverflowingMenuItem[];
  sensitivity?: OverflowingMenuSensitivity;
}

const useStyles = makeStyles(() => ({
  overflowMenuContainer: {
    '& > button': {
      marginRight: 8,

      '&:last-child': {
        marginRight: 0,
      },
    },
  },
  overflowMenuMenu: {
    padding: 3,
  },
}));

export const OverflowingMenu = ({
  menuItems,
  sensitivity = OverflowingMenuSensitivity.MEDIUM,
}: IOverflowingMenuProps) => {
  const { classes } = useStyles();
  const { anchorEl, handleClose, handleMenuOpen } = useMenuLogic();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const filteredMenuItems = menuItems.filter((item) => !item.hide);
  const menuItemsToShow = filteredMenuItems.filter(
    (item) =>
      item.type === OverflowMenuItemType.PRIMARY || item.type === OverflowMenuItemType.SECONDARY,
  );
  const menuItemsHidden = filteredMenuItems.filter(
    (item) => item.type === OverflowMenuItemType.ALWAYS_HIDDEN,
  );

  let buttonItemList: OverflowingMenuItem[] = [];
  let menuItemList: OverflowingMenuItem[] = [];

  if (isMobile) {
    switch (sensitivity) {
      case OverflowingMenuSensitivity.LOW:
      case OverflowingMenuSensitivity.MEDIUM:
        buttonItemList = menuItemsToShow.slice(0, 1);
        menuItemList = menuItemsToShow.slice(1);
        break;
      case OverflowingMenuSensitivity.HIGH:
        menuItemList = menuItemsToShow.slice(0);
        break;
    }
  } else if (isTablet) {
    switch (sensitivity) {
      case OverflowingMenuSensitivity.LOW:
        buttonItemList = menuItemsToShow.slice(0, 2);
        menuItemList = menuItemsToShow.slice(2);
        break;
      case OverflowingMenuSensitivity.MEDIUM:
      case OverflowingMenuSensitivity.HIGH:
        buttonItemList = menuItemsToShow.slice(0, 1);
        menuItemList = menuItemsToShow.slice(1);
        break;
    }
  } else {
    switch (sensitivity) {
      case OverflowingMenuSensitivity.LOW:
        buttonItemList = menuItemsToShow.slice(0, 3);
        menuItemList = menuItemsToShow.slice(3);
        break;
      case OverflowingMenuSensitivity.MEDIUM:
        buttonItemList = menuItemsToShow.slice(0, 2);
        menuItemList = menuItemsToShow.slice(2);
        break;
      case OverflowingMenuSensitivity.HIGH:
        buttonItemList = menuItemsToShow.slice(0, 1);
        menuItemList = menuItemsToShow.slice(1);
        break;
    }
  }

  menuItemList = [...menuItemList, ...menuItemsHidden];

  return (
    <div className={classes.overflowMenuContainer}>
      {buttonItemList.reverse().map((item, index) => (
        <Button
          onClick={item.onClick}
          size="small"
          variant={
            index === buttonItemList.length - 1 && item.type === OverflowMenuItemType.PRIMARY
              ? 'contained'
              : 'outlined'
          }
          key={index}
        >
          {item.label}
        </Button>
      ))}
      {menuItemList.length > 0 && (
        <>
          <IconButton
            aria-controls="list-actions-menu"
            aria-haspopup
            onClick={handleMenuOpen}
            className={classes.overflowMenuMenu}
            size="large"
          >
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            id="list-actions-menu"
            keepMounted
            onClose={handleClose}
            open={Boolean(anchorEl)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuItemList.map((item, index) => (
              <MenuItem onClick={item.onClick} key={index}>
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </div>
  );
};
